import * as THREE from 'three'
import { RAPIER } from '../physics'
import type { BspLoadedLevel } from './bsp-loader'
import { DestructibleManager } from '../world/destructibles'
import { colliderRegistry } from '../world/collider-registry'
import type { DebugMaterialRegistry } from '../debug/debug-materials'
import enemyManifest from '../../assets/data/enemy_manifest.json'
import characterSchemasData from '../../assets/data/character_schemas.json'
import { parseCharacterManifest } from '../ai/character-schema'
import {
  createAuthenticWallMaterial,
  createAuthenticFloorMaterial,
  createAuthenticCeilingMaterial,
} from './textures'
import { BillboardSprite } from '../ai/billboard-sprite'

const characterSchemas = parseCharacterManifest(characterSchemasData)

export interface EnemyManifestEntry {
  id: string
  name: string
  category: 'infantry' | 'heavy' | 'flying' | 'turret' | 'boss' | 'fauna'
  archetype: string
  worldSizeRaw: number
  dimensionsMeters: [number, number, number]
  maxDimensionMeters: number
  pedestalFitScale: number
  hp: number
  maxHp: number
  speed: number
  moveSpeed: number
  attackDamage: number
  fireRate: number
  attackRange: number
  projectileType: string
  projectileSpeed: number
  shieldCapacity: number
  hasRiotShield: boolean
  isFlying: boolean
  isTurret: boolean
  color: number
  lootTable?: Array<{ item: string; weight: number }>
  swatches?: string[]
  billboard: {
    sheetFile: string
    previewFile: string
    frameCount: number
    states: string[]
    angles: number
    frameWidth: number
    frameHeight: number
    sheetCols: number
    sheetRows: number
  }
  provenance: {
    sourceTag: string
    textureParts: Array<{ name: string; width: number; height: number }>
    resolved: boolean
    pkdTexture?: string
  }
}

/**
 * Builds the interactive 3D Rogues' Gallery exhibition showroom.
 * Displays all 35 authentic 1998 Esoteria enemies & characters as animated,
 * 8-directional view-aligned 2D billboard sprites on display pedestals.
 */
export async function buildRoguesGalleryLevel(
  scene: THREE.Scene,
  world: RAPIER.World,
  debugRegistry?: DebugMaterialRegistry,
): Promise<BspLoadedLevel> {
  const roomMeshes: THREE.Mesh[] = []
  const colliders: RAPIER.Collider[] = []
  const destructibleManager = new DestructibleManager(scene)

  const isBrowser = typeof window !== 'undefined'
  const urlParams = isBrowser ? new URLSearchParams(window.location.search) : null
  const categoryQuery = urlParams ? (urlParams.get('biome') || urlParams.get('category'))?.toLowerCase() : null

  let enemies: EnemyManifestEntry[] = (enemyManifest.enemies as unknown) as EnemyManifestEntry[]
  let galleryTitle = "ESOTERIA ENEMY ROGUES' GALLERY"

  if (categoryQuery) {
    if (categoryQuery === 'infantry' || categoryQuery === 'trooper' || categoryQuery === 'cyborg') {
      enemies = enemies.filter((e) => e.category === 'infantry')
      galleryTitle = 'COMBAT INFANTRY & CYBORG TROOPER WING'
    } else if (categoryQuery === 'heavy' || categoryQuery === 'mech' || categoryQuery === 'tank') {
      enemies = enemies.filter((e) => e.category === 'heavy')
      galleryTitle = 'HEAVY ARMORED MECH & VEHICLE PAVILION'
    } else if (categoryQuery === 'flying' || categoryQuery === 'drone' || categoryQuery === 'air') {
      enemies = enemies.filter((e) => e.category === 'flying')
      galleryTitle = 'AERIAL RECON DRONE & GUNSHIP HANGAR'
    } else if (categoryQuery === 'boss' || categoryQuery === 'overlord') {
      enemies = enemies.filter((e) => e.category === 'boss')
      galleryTitle = 'APEX BOSS & CYBERNETIC OVERLORD SANCTUM'
    } else if (categoryQuery === 'turret' || categoryQuery === 'defense') {
      enemies = enemies.filter((e) => e.category === 'turret')
      galleryTitle = 'AUTOMATED DEFENSE TURRET BATTERY'
    } else if (categoryQuery === 'fauna' || categoryQuery === 'aquatic' || categoryQuery === 'water') {
      enemies = enemies.filter((e) => e.category === 'fauna')
      galleryTitle = 'SUB-AQUATIC & NATIVE FAUNA SPECIMENS'
    }
  }

  // Grid layout parameters
  const cols = Math.min(6, Math.max(2, enemies.length))
  const spacingX = 5.5
  const spacingZ = 6.5
  const rows = Math.max(1, Math.ceil(enemies.length / cols))

  const hallWidth = cols * spacingX + 10.0
  const hallLength = rows * spacingZ + 24.0
  const hallHeight = 8.0

  const floorY = 0.0
  const ceilingY = hallHeight
  const centerZ = -(rows * spacingZ) / 2 - 5.0

  // 1. Floor & Ceiling Materials
  const floorMat = createAuthenticFloorMaterial()
  const ceilingMat = createAuthenticCeilingMaterial()
  const wallMat = createAuthenticWallMaterial()

  // Floor
  const floorGeom = new THREE.PlaneGeometry(hallWidth, hallLength)
  floorGeom.rotateX(-Math.PI / 2)
  const floorMesh = new THREE.Mesh(floorGeom, floorMat)
  floorMesh.position.set(0, floorY, centerZ)
  scene.add(floorMesh)
  roomMeshes.push(floorMesh)

  // Rapier Floor Collider
  const floorBodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(0, floorY, centerZ)
  const floorBody = world.createRigidBody(floorBodyDesc)
  const floorColDesc = RAPIER.ColliderDesc.cuboid(hallWidth / 2, 0.1, hallLength / 2)
  const floorCollider = world.createCollider(floorColDesc, floorBody)
  colliders.push(floorCollider)

  // Ceiling
  const ceilingGeom = new THREE.PlaneGeometry(hallWidth, hallLength)
  ceilingGeom.rotateX(Math.PI / 2)
  const ceilingMesh = new THREE.Mesh(ceilingGeom, ceilingMat)
  ceilingMesh.position.set(0, ceilingY, centerZ)
  scene.add(ceilingMesh)
  roomMeshes.push(ceilingMesh)

  // Perimeter Walls
  const wallThickness = 0.5
  const wallDefs = [
    { w: hallWidth, h: hallHeight, d: wallThickness, x: 0, y: hallHeight / 2, z: 6.0, name: 'Rogues Gallery Portal Entrance' },
    { w: hallWidth, h: hallHeight, d: wallThickness, x: 0, y: hallHeight / 2, z: -(rows * spacingZ + 18.0), name: 'Rogues Gallery Apex Wall' },
    { w: wallThickness, h: hallHeight, d: hallLength, x: -hallWidth / 2, y: hallHeight / 2, z: centerZ, name: 'Rogues Gallery West Wing Wall' },
    { w: wallThickness, h: hallHeight, d: hallLength, x: hallWidth / 2, y: hallHeight / 2, z: centerZ, name: 'Rogues Gallery East Wing Wall' },
  ]

  for (const w of wallDefs) {
    const geom = new THREE.BoxGeometry(w.w, w.h, w.d)
    const mesh = new THREE.Mesh(geom, wallMat)
    mesh.position.set(w.x, w.y, w.z)
    scene.add(mesh)
    roomMeshes.push(mesh)

    const bDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(w.x, w.y, w.z)
    const body = world.createRigidBody(bDesc)
    const cDesc = RAPIER.ColliderDesc.cuboid(w.w / 2, w.h / 2, w.d / 2)
    const col = world.createCollider(cDesc, body)
    colliders.push(col)

    colliderRegistry.register(col.handle, {
      id: w.name.toLowerCase().replace(/\s+/g, '_'),
      name: w.name,
      category: 'wall',
      sourceTag: 'ESOTERIA/ROGUES.E3 : Gallery Wall',
    })

    if (debugRegistry) {
      debugRegistry.registerMesh(mesh, 'wall', wallMat, {
        id: w.name.toLowerCase().replace(/\s+/g, '_'),
        name: w.name,
        sourceTag: 'ESOTERIA/ROGUES.E3 : Gallery Wall',
      })
    }
  }

  // 2. Atmospheric & Directional Lighting
  const ambLight = new THREE.AmbientLight(0xddeeff, 2.5)
  scene.add(ambLight)

  const dirLight = new THREE.DirectionalLight(0xffffff, 2.0)
  dirLight.position.set(10, 20, 10)
  scene.add(dirLight)

  // 3. Exhibition Pedestal Materials & Accent Rims
  const pedestalMat = new THREE.MeshStandardMaterial({
    color: 0x14161d,
    roughness: 0.35,
    metalness: 0.7,
  })
  const neonAmberMat = new THREE.MeshBasicMaterial({ color: 0xffaa00 }) // Infantry / Cyborgs
  const neonCyanMat = new THREE.MeshBasicMaterial({ color: 0x00f0ff })  // Flying Drones / Aquatic
  const neonMagentaMat = new THREE.MeshBasicMaterial({ color: 0xff007f }) // Heavy Mechs / Bosses
  const neonYellowMat = new THREE.MeshBasicMaterial({ color: 0xffdd00 })  // Turrets / Defense

  const startX = -((cols - 1) * spacingX) / 2
  const startZ = -8.0 // Starts 8m down the avenue

  const billboardSprites: BillboardSprite[] = []

  for (let i = 0; i < enemies.length; i++) {
    const enemy = enemies[i]
    const c = i % cols
    const r = Math.floor(i / cols)

    const posX = startX + c * spacingX
    const posZ = startZ - r * spacingZ
    const pedestalH = 0.6
    const pedestalW = 2.2
    const pedestalD = 2.2

    // Pedestal Base Mesh
    const pedGeom = new THREE.BoxGeometry(pedestalW, pedestalH, pedestalD)
    const pedMesh = new THREE.Mesh(pedGeom, pedestalMat)
    pedMesh.position.set(posX, floorY + pedestalH / 2, posZ)
    scene.add(pedMesh)
    roomMeshes.push(pedMesh)

    // Glowing Category Trim Rim
    const trimMat =
      enemy.category === 'boss' || enemy.category === 'heavy'
        ? neonMagentaMat
        : enemy.category === 'flying' || enemy.category === 'fauna'
          ? neonCyanMat
          : enemy.category === 'turret'
            ? neonYellowMat
            : neonAmberMat

    const trimGeom = new THREE.BoxGeometry(pedestalW + 0.12, 0.05, pedestalD + 0.12)
    const trimMesh = new THREE.Mesh(trimGeom, trimMat)
    trimMesh.position.set(posX, floorY + 0.025, posZ)
    scene.add(trimMesh)

    // Pedestal Collider
    const pedBodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(posX, floorY + pedestalH / 2, posZ)
    const pedBody = world.createRigidBody(pedBodyDesc)
    const pedColDesc = RAPIER.ColliderDesc.cuboid(pedestalW / 2, pedestalH / 2, pedestalD / 2)
    const pedCol = world.createCollider(pedColDesc, pedBody)
    colliders.push(pedCol)

    // 4. Instantiation of View-Aligned Billboard Sprite
    const [wMeters, hMeters] = enemy.dimensionsMeters
    const displayScale = enemy.pedestalFitScale ?? 1.0
    const finalW = wMeters * displayScale
    const finalH = hMeters * displayScale

    const sprite = new BillboardSprite({
      sheetTexturePath: enemy.billboard.sheetFile,
      previewTexturePath: enemy.billboard.previewFile,
      widthMeters: finalW,
      heightMeters: finalH,
      billboardMode: enemy.isFlying ? 'spherical' : 'cylindrical',
      cols: enemy.billboard.sheetCols,
      rows: enemy.billboard.sheetRows,
      animations: (enemy.billboard as any).animations,
      characterSchema: characterSchemas[enemy.id],
      initialState: 'walk',
      debugRegistry,
    })

    // Position sprite atop the pedestal
    sprite.mesh.position.set(posX, floorY + pedestalH + 0.02, posZ)
    sprite.setFacingYaw(0) // Default facing forward (-Z)
    sprite.mesh.name = `enemy_sprite_${enemy.id}`
    scene.add(sprite.mesh)
    roomMeshes.push(sprite.mesh)
    billboardSprites.push(sprite)

    // WTHIT Provenance & Debug Card
    const meta = {
      id: enemy.id,
      name: `${enemy.name} [${enemy.category.toUpperCase()}]`,
      sourceTag: enemy.provenance.sourceTag,
      dimensions: [wMeters, hMeters, enemy.dimensionsMeters[2]] as [number, number, number],
      availableSwatches: enemy.swatches || ['standard'],
      getDetails: () => {
        const lootStr = enemy.lootTable
          ? enemy.lootTable.map((l) => `${l.item} (${l.weight}%)`).join(', ')
          : 'None'
        const partsCount = enemy.provenance.textureParts?.length ?? 0
        const animStateName = sprite.getDisplayStateName()
        return {
          'ID': enemy.id,
          'Name': enemy.name,
          'Category': enemy.category.toUpperCase(),
          'Archetype': enemy.archetype,
          'HP': `${enemy.hp} / ${enemy.maxHp}`,
          'Shield': enemy.shieldCapacity > 0 ? `${enemy.shieldCapacity} (Riot Shield: ${enemy.hasRiotShield})` : 'None',
          'Speed': `${enemy.moveSpeed} m/s`,
          'Attack Damage': `${enemy.attackDamage} (${enemy.projectileType})`,
          'Fire Rate': `${enemy.fireRate} /s (Range: ${enemy.attackRange}m)`,
          'Dimensions': `${wMeters}m × ${hMeters}m (1:1 Metric)`,
          'Scale on Plinth': displayScale === 1.0 ? '1:1 (Actual)' : `${(displayScale * 100).toFixed(0)}% (Plinth Fit)`,
          'Anim State': `${animStateName} [KeyT: cycle anim] (frame ${sprite.getCurrentFrameIndex() + 1})`,
          'View Angle': `${sprite.getCurrentAngleName()} [Octant ${sprite.getCurrentAngleIndex()}]`,
          'Facing Yaw': `${((sprite.getFacingYaw() * 180) / Math.PI).toFixed(1)}°`,
          'Loot Drop Table': lootStr,
          'Sprite Sheet': enemy.billboard.sheetFile,
          'Texture Parts': partsCount > 0 ? `${partsCount} OBJTEXTS parts` : 'Authentic 22-Angle PKDTEXTS',
          'Provenance': enemy.provenance.sourceTag,
        }
      },
    }

    colliderRegistry.register(pedCol.handle, {
      id: enemy.id,
      name: `${enemy.name} [${enemy.category.toUpperCase()}]`,
      category: 'enemy',
      sourceTag: enemy.provenance.sourceTag,
    })

    if (debugRegistry) {
      debugRegistry.registerMesh(
        sprite.mesh,
        'enemy',
        sprite.material,
        meta,
      )
    }

    // Attach sprite reference to mesh userData for interactive state cycling
    sprite.mesh.userData = {
      enemyId: enemy.id,
      billboardSprite: sprite,
      currentTexture: enemy.billboard.sheetFile,
      availableSwatches: enemy.swatches,
      cycleState: () => {
        const newState = sprite.cycleState()
        return {
          texture: enemy.billboard.sheetFile,
          state: newState,
          index: enemy.billboard.states.indexOf(newState) + 1,
          total: enemy.billboard.states.length,
        }
      },
    }
  }

  // Hook into scene render loop to update billboard orientation and real-time animation relative to camera
  let lastRenderTime = typeof performance !== 'undefined' ? performance.now() : Date.now()
  const originalOnBeforeRender = scene.onBeforeRender
  scene.onBeforeRender = (renderer, sceneObj, camera, geometry, material, group) => {
    if (originalOnBeforeRender) {
      originalOnBeforeRender.call(scene, renderer, sceneObj, camera, geometry, material, group)
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const elapsed = (now - lastRenderTime) / 1000.0
    // Prevent multiple render passes in a single frame from skipping elapsed time
    if (elapsed >= 0.004) {
      const delta = Math.min(0.1, elapsed)
      lastRenderTime = now
      const camPos = camera.position
      for (let i = 0; i < billboardSprites.length; i++) {
        billboardSprites[i].update(delta, camPos)
      }
    }
  }

  const spawnPosition = new THREE.Vector3(0, 1.2, 0.0)
  const facingRad = 0.0

  return {
    spawnPosition,
    facingRad,
    roomMeshes,
    doors: [],
    destructibles: [],
    destructibleManager,
    elevators: [],
    colliders,
    targets: [],
    messages: [
      {
        text: `${galleryTitle}: Exhibiting ${enemies.length} animated 2D billboard enemies with 8-directional view tracking. Aim at any pedestal to inspect stats [WTHIT] or press KeyT to cycle animation states.`,
        triggerObjectId: null,
      },
    ],
  }
}
